import * as schema from '@fullstory/client-api';
import { DocumentCriterion } from '@fullstory/client-api';
import { ConverterSpec, DocumentCriteriaNullable } from './shared';
import { equalDocumentCriteria } from './criteria';
import { EventConverterMap } from './event';
import { DependentConverterMap } from './dependent';
import { UserConverterMap } from './user';
import { NONE, SearchType, UserType, EventType, DependentType } from '@fullstory/search-types';
import { DependentEventFilter, EventFilter, UserFilter } from '@fullstory/proto/fs/api/segment';
import {
  DefinedEventIdField,
  NamedElementIdField,
} from '@fullstory/client-api/goimports/fs_search_doc_fields_special';

type ParsedCriteria = {
  primary: EventType | null;
  dependents: DependentType[];
};

type ValidatedParsedCriteria = ParsedCriteria & {
  ok: boolean;
};

export function eventTypeToFilter(et: EventType): EventFilter {
  return typeToFilter(et, EventConverterMap);
}

export function userTypeToFilter(ut: UserType): UserFilter {
  return typeToFilter(ut, UserConverterMap);
}

export function dependentTypeToFilter(dt: DependentType): DependentEventFilter {
  return typeToFilter(dt, DependentConverterMap);
}

export function typeToFilter(st: EventType, map: EventConverterMap): EventFilter | null;
export function typeToFilter(st: UserType, map: UserConverterMap): UserFilter | null;
export function typeToFilter(
  st: DependentType,
  map: DependentConverterMap,
): DependentEventFilter | null;
export function typeToFilter(
  st: EventType | UserType | DependentType,
  map: EventConverterMap | DependentConverterMap | UserConverterMap,
): EventFilter | UserFilter | DependentEventFilter {
  let spec = map[st.kind][st.subkind] as ConverterSpec<SearchType>;
  return spec.searchTypeToFilter(st, eventTypeToFilter, dependentTypeToFilter);
}

export function eventFilterToEventType(ef: EventFilter | null, flags: schema.Flags): EventType {
  return filterToType(ef, flags, EventConverterMap, { kind: 'any', subkind: NONE, value: NONE });
}

export function userFilterToUserType(uf: UserFilter, flags: schema.Flags): UserType {
  return filterToType(uf, flags, UserConverterMap, { kind: 'any', subkind: NONE, value: NONE });
}

export function dependentFilterToDependentType(
  df: DependentEventFilter,
  flags: schema.Flags,
): DependentType {
  return filterToType(df, flags, DependentConverterMap, { kind: NONE, subkind: NONE, value: NONE });
}

function filterToType(
  f: EventFilter | null,
  flags: schema.Flags,
  map: EventConverterMap,
  fallbackSearchType: EventType,
): EventType | null;
function filterToType(
  f: DependentEventFilter | null,
  flags: schema.Flags,
  map: DependentConverterMap,
  fallbackSearchType: DependentType,
): DependentType | null;
function filterToType(
  f: UserFilter | null,
  flags: schema.Flags,
  map: UserConverterMap,
  fallbackSearchType: UserType,
): UserType | null;
function filterToType(
  sf: EventFilter | DependentEventFilter | UserFilter | null,
  flags: schema.Flags,
  map: EventConverterMap | DependentConverterMap | UserConverterMap,
  fallbackSearchType: EventType | DependentType | UserType,
): EventType | DependentType | UserType | null {
  // Null sf should convert to fallbackSearchType
  sf = sf ?? {};
  let [kind] = Object.keys(sf);
  if (kind === undefined) {
    kind = fallbackSearchType.kind;
  }

  let subkind = sf[kind] ? Object.keys(sf[kind])[0] : undefined;
  if (subkind === undefined) {
    // Fallback and attempt using NONE because some SearchDefinitions only have a depth of 1.
    // In which case, the corresponding SearchType has a subkind of NONE by convention of being flat.
    subkind = fallbackSearchType.subkind;
  }

  const spec = map[kind][subkind] as ConverterSpec<SearchType>;

  if (!spec) {
    return null;
  }

  if (spec.disabled && spec.disabled(flags)) {
    return null;
  }

  return spec.filterToSearchType(
    sf,
    ef => eventFilterToEventType(ef, flags),
    df => dependentFilterToDependentType(df, flags),
  );
}

export function fromEventCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
): ValidatedParsedCriteria {
  // Determine the event.
  var { result: primary, remainder } = fromCriteria(x, flags, EventConverterMap);
  if (primary == null) {
    return { primary: null, dependents: [], ok: false }; // no matching event found
  }

  // Determine the dependent events.
  const dependents: DependentType[] = [];
  while (remainder.length != 0) {
    var { result: dep, remainder } = fromCriteria(remainder, flags, DependentConverterMap);
    if (dep == null) {
      return { primary: null, dependents: [], ok: false }; // no matching dependent event found
    }
    dependents.push(dep);
  }

  return { primary, dependents, ok: true };
}

export function fromUserTipCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
): UserType | null {
  const { result: user, remainder } = fromCriteria(x, flags, UserConverterMap);
  if (user == null) {
    return null;
  }
  if (remainder.length != 0) {
    // excess leftover in the input
    return null;
  }
  return user;
}

function fromCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
  map: EventConverterMap,
): { result: EventType | null; remainder: schema.DocumentCriterion[] };
function fromCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
  map: DependentConverterMap,
): { result: DependentType | null; remainder: schema.DocumentCriterion[] };
function fromCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
  map: UserConverterMap,
): { result: UserType | null; remainder: schema.DocumentCriterion[] };

// Greedily consumes the supplied criteria, attempting to match it with
// one of the known shapes in the converter maps. If there is a match,
// returns the result and any leftover criteria.
function fromCriteria(
  x: schema.DocumentCriterion[],
  flags: schema.Flags,
  map: EventConverterMap | DependentConverterMap | UserConverterMap,
): {
  result: SearchType | null;
  remainder: schema.DocumentCriterion[];
} {
  for (let i = x.length; i >= 0; i--) {
    const currentCriteriaSlice = x.slice(0, i);

    for (const k in map) {
      // First, try strict equality for each of the subkinds.
      for (const sk in map[k]) {
        const spec = map[k][sk] as ConverterSpec<SearchType>; // TODO(nishanth): avoid the type assertion?
        if (spec.disabled && spec.disabled(flags)) {
          continue;
        }
        const extracted =
          spec.extractFromCriteria && spec.extractFromCriteria(currentCriteriaSlice);
        if (extracted == null) {
          // could not extract
          continue;
        }
        const criteriaWithValue = spec.criteria(extracted);
        if (equalDocumentCriteria(currentCriteriaSlice, criteriaWithValue)) {
          return { result: extracted, remainder: x.slice(i) };
        }
      }

      // Next, try loose equality for each of the subkinds.
      for (const sk in map[k]) {
        const spec = map[k][sk] as ConverterSpec<SearchType>; // TODO(nishanth): avoid the type assertion?
        if (spec.disabled && spec.disabled(flags)) {
          continue;
        }
        const extracted =
          spec.extractFromCriteria && spec.extractFromCriteria(currentCriteriaSlice);
        if (extracted == null) {
          // could not extract
          continue;
        }
        const criteriaWithValue = spec.criteria(extracted);
        if (equalDocumentCriteria(currentCriteriaSlice, criteriaWithValue, true)) {
          return { result: extracted, remainder: x.slice(i) };
        }
      }
    }
  }

  return { result: null, remainder: x };
}

const CRITERIA_ORDER: [Set<string>, Set<string>, Set<string>, Set<string>] = [
  // the following are always the start of a high level filter, they should be sorted first
  new Set(['EventType', 'EventModSuspicious', DefinedEventIdField]),
  // the following are always attached to the primary filter and thus should be sorted next
  new Set([
    'EventModFrustrated',
    'EventModError',
    'EventModDead',
    'LoadEventTime',
    'LoadDomContentTime',
    'LoadFirstPaintTime',
    'EventCustomName',
  ]),
  // the following are almost always attached to the primary filter, treat them with a little higher priority to ensure so
  new Set(['EventTargetText', '$EventTargetSelector', NamedElementIdField]),
  // and finally, the following are sometimes part of the primary filter or the dependent filter and should be sorted after
  new Set([
    'EventVarErrorKind',
    'AppViewName',
    'PageUrl',
    'PageUrlHost',
    'PageUrlPath',
    'PageUrlQuery',
    'PageUrlHash',
    'ReqUrl',
    'ReqUrlHost',
    'ReqUrlPath',
    'ReqUrlQuery',
    'ReqUrlHash',
  ]),
  // all other fields will fall into place at the end and only exist as dependent filters
];

// Field=EventType should always be first
// Non mutative
export function maybeReorderCriteria(crit: DocumentCriteriaNullable): DocumentCriterion[] {
  return crit.slice().sort((a: DocumentCriterion, b: DocumentCriterion) => {
    let a_order: number = CRITERIA_ORDER.length;
    let b_order: number = CRITERIA_ORDER.length;

    for (let i = 0; i < CRITERIA_ORDER.length; i++) {
      if (CRITERIA_ORDER[i].has(a.Field)) {
        a_order = i;
        break;
      }
    }

    for (let i = 0; i < CRITERIA_ORDER.length; i++) {
      if (CRITERIA_ORDER[i].has(b.Field)) {
        b_order = i;
        break;
      }
    }

    if (a_order > b_order) {
      return 1;
    } else if (b_order > a_order) {
      return -1;
    }

    return 0;
  });
}
